/**
 * Turning whatever React caught into something an operator can read out loud
 * and we can search for in PostHog / the booth log.
 *
 * Kept free of React and of the preload bridge on purpose: this runs after the
 * renderer tree has already thrown, so it must not be able to throw itself,
 * and it has to be unit-testable under plain node.
 */

/** Stacks go into a log line and a PostHog property; keep both bounded. */
const MAX_STACK_CHARS = 2000
/** The operator reads this off the screen, so it has to stay one short line. */
const MAX_SUMMARY_CHARS = 160

export interface CrashReport {
  /** Error class name, e.g. `TypeError`. `Error` when the thrown value had none. */
  name: string
  /** Error message, or the stringified thrown value. Never empty. */
  message: string
  /** JS stack, truncated. `null` when the thrown value carried none. */
  stack: string | null
  /** React's component stack, truncated. `null` when React gave none. */
  componentStack: string | null
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text
}

/** `String(value)` without ever throwing — a thrown Proxy can refuse it. */
function safeString(value: unknown): string {
  try {
    return String(value)
  } catch {
    return 'unreadable error value'
  }
}

/**
 * Normalise a caught value into a `CrashReport`.
 *
 * Anything can be thrown in JS, and the booth has non-Error throws in the wild
 * (a rejected `fetch` body, a string from a vendor SDK), so this does not
 * assume `error instanceof Error`.
 */
export function describeCrash(error: unknown, componentStack?: string | null): CrashReport {
  const isError = error instanceof Error
  const rawMessage = isError ? error.message : safeString(error)
  const rawStack = isError && typeof error.stack === 'string' ? error.stack : null

  return {
    name: (isError && error.name) || 'Error',
    message: rawMessage.trim() || 'Unknown error',
    stack: rawStack ? truncate(rawStack, MAX_STACK_CHARS) : null,
    componentStack: componentStack?.trim() ? truncate(componentStack.trim(), MAX_STACK_CHARS) : null
  }
}

/**
 * The one line shown on the crash screen and sent to support. Deliberately
 * `Name: message` and nothing else — no stack, no file paths, nothing a guest
 * standing at the booth could mistake for instructions.
 */
export function crashSummaryLine(report: CrashReport): string {
  return truncate(`${report.name}: ${report.message}`, MAX_SUMMARY_CHARS)
}
